<template>
  <main>
    <div class="container container-fluid">
      <al-modal
        :heading="$t('cardDetails.deleteHeading')"
        :close-label="$t('button.cancel')"
        :cta-label="$t('button.delete')"
        name="delete-card-modal"
        @dismiss="cancelDelete"
        @cta="doDelete">
        <div class="row align-left margin-bottom-sm">
          <div class="col-full">
            <al-card-preview
              v-if="cardToBeDeleted"
              :img="cardToBeDeleted.artUrl"
              :brand="getCardBrand"
              :number="`•••• ${cardToBeDeleted.primaryAccountNumberSuffix}`"
              name="delete-card-preview" />
          </div>
        </div>
      </al-modal>
      <div class="container">
        <al-notify v-if="successToken" success name="success-msg">
          {{ $t(successToken) }}
        </al-notify>
        <al-notify v-else-if="errorToken" error name="error-msg">
          {{ $t(errorToken) }}
        </al-notify>
        <div class="row">
          <div class="col">
            <h1 class="heading-xl align-center-space-between">
              {{ $t('cardDetails.heading') }}
            </h1>
          </div>
        </div>
        <div class="card">
          <div class="card__item">
            <div class="row">
              <div class="col">
                <al-card-preview
                  :img="card.artUrl"
                  :brand="getCardBrand"
                  :number="`•••• ${card.primaryAccountNumberSuffix}`"
                  :name="`card-list-preview`" />
              </div>
            </div>
          </div>
          <div class="card__item">
            <div v-if="cardholderName" class="row">
              <div class="col">
                <h2 class="heading-md">{{ $t('cardDetails.name') }}</h2>
                <h3 class="card-details__value heading-sm">
                  {{ cardholderName }}
                </h3>
              </div>
            </div>
            <div class="row">
              <div class="col">
                <h2 class="heading-md">
                  {{ $t('cardDetails.expiration') }}
                </h2>
                <h3 class="card-details__value heading-sm">
                  {{ card.fpanExpiration }}
                </h3>
              </div>
            </div>
            <template v-if="!isRestrictedLocale">
              <div class="row">
                <div class="col-3">
                  <h2 class="heading-md">
                    {{ $t('cardDetails.billingInformation') }}
                  </h2>
                </div>
                <div v-if="!cardError(card)" class="col-1 align-right">
                  <button
                    v-analytics:click="
                      areBillingDetailsAvailable(card)
                        ? $EVENTS.EDIT_BILLING_ADDRESS
                        : $EVENTS.ADD_BILLING_ADDRESS
                    "
                    :aria-label="manageBillingType"
                    class="btn--secondary heading-md"
                    data-test="manage-billing-address-btn"
                    @click="manageBillingAddress">
                    {{ manageBillingType }}
                  </button>
                </div>
              </div>
              <div class="row">
                <div class="col">
                  <tux-address-label
                    v-if="areBillingDetailsAvailable(card)"
                    class="heading-sm"
                    :address="getAddressLabel(card.billingAddress)" />
                  <h3 v-else class="card-details__value heading-sm">
                    {{ $t('cardDetails.noBillingInformation') }}
                  </h3>
                </div>
              </div>
            </template>
          </div>
        </div>

        <div class="row row-vertical-align-bottom">
          <div class="col-full align-center">
            <button
              v-analytics:click="$EVENTS.DELETE_BUTTON"
              v-toggle:al-modal="'delete-card-modal'"
              data-test="delete-card-btn"
              class="fill uppercase"
              :aria-label="getCardAccessibilityLabel(card)"
              @click="setCardToBeDeleted(card)">
              {{ $t('cardDetails.delete') }}
            </button>
          </div>
          <div class="col-full align-center">
            <back-button v-analytics:click="$EVENTS.GO_BACK_BUTTON" :location="backButtonRoute">
              {{ $t('cardDetails.return') }}
            </back-button>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
import { AlCardPreview, AlModal, AlNotify } from '@mc-alberta/alcapone'
import { TuxAddressLabel } from '@mc-alberta/tuxedo'
import { mapActions, mapGetters, mapState } from 'vuex'
import BackButton from '@/components/BackButton'
import { CARD_STATUS, LOADERS, ROUTES } from '@/constants'
import { formatAddressMasks } from '@/utils/format-addresses.js'

export default {
  name: ROUTES.CARD_DETAILS,
  components: {
    AlCardPreview,
    AlModal,
    AlNotify,
    BackButton,
    TuxAddressLabel
  },
  data() {
    return {
      backButtonRoute: { name: ROUTES.LIST_CARDS },
      card: null,
      cardToBeDeleted: null,
      errorToken: '',
      successToken: ''
    }
  },
  computed: {
    ...mapGetters(['sortedCards', 'cardByCardId', 'locale']),
    ...mapState({
      serviceLayerError: (state) => state.serviceLayerError
    }),
    cardholderName() {
      return this.card.billingAddress?.fullName
    },
    getCardBrand() {
      return (
        this.card.shortProductDescription || this.card.issuerName || this.card.paymentNetworkName
      )
    },
    manageBillingType() {
      return this.areBillingDetailsAvailable(this.card)
        ? this.$t('cardDetails.edit')
        : this.$t('cardDetails.add')
    },
    isRestrictedLocale() {
      return this.$i18nData.isCountryRestricted(this.locale)
    }
  },
  created() {
    if (!this.$route.params.cardId) {
      this.$router.replace({ name: ROUTES.LIST_CARDS })
    }

    this.card = this.cardByCardId(this.$route.params.cardId)
    this.successToken = this.$route.params.successToken || ''
    this.errorToken = this.$route.params.errorToken || this.cardError(this.card)
  },
  methods: {
    ...mapActions([
      'clearServiceLayerError',
      'deleteCard',
      'stopLoader',
      'startLoader',
      'stopLoaderWithSuccess'
    ]),
    manageBillingAddress() {
      this.$router.push({
        name: ROUTES.ADD_BILLING,
        params: {
          hasBillingAddress: this.areBillingDetailsAvailable(this.card),
          cardId: this.card.id
        }
      })
    },
    setCardToBeDeleted(card) {
      this.cardToBeDeleted = card
    },
    cancelDelete() {
      this.$analytics.track(this.$EVENTS.MODAL_CANCEL_BUTTON)
      this.cardToBeDeleted = null
    },
    async doDelete() {
      // prevents a race condition issue where the user clicks delete while another delete is processing
      if (!this.cardToBeDeleted) return

      this.clearServiceLayerError()
      this.startLoader(LOADERS.UPDATE_PROFILE)
      this.$analytics.track(this.$EVENTS.MODAL_DELETE_BUTTON)

      await this.deleteCard(this.cardToBeDeleted.id)
      this.errorToken = this.serviceLayerError ? 'cardDetails.deleteError' : ''

      if (this.serviceLayerError) {
        this.stopLoader(LOADERS.UPDATE_PROFILE)
      } else {
        this.stopLoaderWithSuccess(LOADERS.UPDATE_PROFILE)
      }

      if (!this.errorToken) {
        this.$router.replace({
          name: ROUTES.LIST_CARDS,
          params: { isCardDeleted: true }
        })
      }
    },
    areBillingDetailsAvailable(card) {
      const { createTime, fullName, id, lastUsedTime, ...billingDetails } =
        card?.billingAddress || {}

      return Object.values(billingDetails).filter(Boolean).length > 0
    },
    cardError(card) {
      let errorMessage = ''

      if (card.status === CARD_STATUS.EXPIRED) {
        errorMessage = this.$t('cardDetails.expired')
      } else if (card.status === CARD_STATUS.SUSPENDED) {
        errorMessage = this.$t('cards.unavailable')
      }

      return errorMessage
    },
    getAddressLabel(billingAddress) {
      const addressLabel = Object.assign({}, billingAddress)
      delete addressLabel.fullName
      delete addressLabel.name
      return formatAddressMasks(addressLabel)
    },
    getCardAccessibilityLabel(card) {
      const cardLocaleSlots = {
        name: card.issuerName || card.paymentNetworkName,
        suffix: card.primaryAccountNumberSuffix
      }

      return this.$t('accessibility.deleteCard', cardLocaleSlots)
    }
  }
}
</script>

<style lang="scss" scoped>
.card-details__value {
  color: var(--src-color-gray-high);
}
</style>
